import { Controller } from "@hotwired/stimulus";
import { escapeHtml } from "../utils.js";

export default class extends Controller {
  static targets = [
    "button",
    "menu",
    "label",
    "caret",
    "search",
    "options",
    "option",
    "pills",
    "pill",
    "checkbox",
    "noMatches",
  ];

  connect() {
    this.isOpen = false;
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
  }

  disconnect() {
    document.removeEventListener("click", this.handleOutsideClick);
  }

  toggle(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.isOpen = true;
    this.menuTarget.classList.remove("hidden");
    this.caretTarget.classList.add("rotate-180");
    document.addEventListener("click", this.handleOutsideClick);

    // Focus search input if present
    if (this.hasSearchTarget) {
      this.searchTarget.focus();
    }
  }

  close() {
    this.isOpen = false;
    this.menuTarget.classList.add("hidden");
    this.caretTarget.classList.remove("rotate-180");
    document.removeEventListener("click", this.handleOutsideClick);
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.close();
    }
  }

  filter(event) {
    const query = event.target.value.toLowerCase().trim();
    let visibleCount = 0;

    this.optionTargets.forEach((option) => {
      // Support both generic 'itemName' and legacy 'lotName' for backwards compatibility
      const itemName = option.dataset.itemName || option.dataset.lotName || "";
      const matches = itemName.includes(query);
      option.classList.toggle("hidden", !matches);
      if (matches) visibleCount++;
    });

    // Show/hide "no matches" message
    if (this.hasNoMatchesTarget) {
      this.noMatchesTarget.classList.toggle("hidden", visibleCount > 0);
    }
  }

  updateLabel() {
    if (!this.hasLabelTarget) return;

    const checkboxes = this.optionsTarget.querySelectorAll(
      'input[type="checkbox"]',
    );
    const selectedCount = Array.from(checkboxes).filter(
      (cb) => cb.checked,
    ).length;

    if (selectedCount === 0) {
      this.labelTarget.textContent = this.element.dataset.allLabel || "All";
    } else if (selectedCount === 1) {
      const selectedCheckbox = Array.from(checkboxes).find((cb) => cb.checked);
      const label = selectedCheckbox
        ?.closest("label")
        ?.querySelector("span")?.textContent;
      this.labelTarget.textContent = label || `${selectedCount} selected`;
    } else {
      this.labelTarget.textContent = `${selectedCount} selected`;
    }
  }

  // Update pills when checkbox changes
  updatePills(event) {
    if (!this.hasPillsTarget) return;

    const checkbox = event.target;
    const isChecked = checkbox.checked;
    const option = checkbox.closest(
      "[data-multiselect-dropdown-target='option']",
    );
    // Falls back to the checkbox value when the option has no explicit item
    // id, preserving existing single-collection dropdowns. Dropdowns that
    // group multiple collections (e.g. lots + lot blocks) with overlapping
    // numeric ids rely on the option's namespaced item id instead.
    const itemId = option?.dataset.itemId || checkbox.value;
    const itemName = option?.querySelector("span")?.textContent || "";

    if (isChecked) {
      this.addPill(itemId, itemName);
    } else {
      this.removePill(itemId);
    }
  }

  addPill(itemId, itemName) {
    // Check if pill already exists
    const existingPill = this.pillTargets.find(
      (pill) => pill.dataset.itemId === itemId,
    );
    if (existingPill) return;

    const pill = document.createElement("span");
    pill.className = "gap-1 py-2 badge badge-sm badge-primary badge-soft";
    pill.dataset.multiselectDropdownTarget = "pill";
    pill.dataset.itemId = itemId;
    pill.innerHTML = `
      <span class="max-w-24 truncate">${escapeHtml(itemName)}</span>
      <button type="button" class="transition-colors hover:text-error" data-action="click->multiselect-dropdown#removeItem">
        <i class="text-xs i-ph-x"></i>
      </button>
    `;
    this.pillsTarget.appendChild(pill);
    this.updatePillsContainerPadding();
  }

  removePill(itemId) {
    const pill = this.pillTargets.find(
      (pill) => pill.dataset.itemId === itemId,
    );
    if (pill) {
      pill.remove();
      this.updatePillsContainerPadding();
    }
  }

  updatePillsContainerPadding() {
    if (!this.hasPillsTarget) return;

    const hasPills = this.pillTargets.length > 0;
    this.pillsTarget.classList.toggle("pb-2", hasPills);
  }

  // Remove item via pill click
  removeItem(event) {
    event.preventDefault();
    event.stopPropagation();

    const pill = event.target.closest(
      "[data-multiselect-dropdown-target='pill']",
    );
    if (!pill) return;

    const itemId = pill.dataset.itemId;

    // Look up the checkbox via its option's item id first, so dropdowns
    // grouping multiple collections (e.g. lots + lot blocks) with
    // overlapping numeric ids don't uncheck the wrong item.
    const option = this.optionsTarget.querySelector(
      `[data-multiselect-dropdown-target="option"][data-item-id="${itemId}"]`,
    );
    const checkbox =
      option?.querySelector('input[type="checkbox"]') ||
      this.optionsTarget.querySelector(
        `input[type="checkbox"][value="${itemId}"]`,
      );
    if (checkbox) {
      checkbox.checked = false;
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
    }

    // Remove the pill
    pill.remove();

    // Update label if using dropdown mode
    this.updateLabel();
  }

  clearAll(event) {
    event.preventDefault();
    const checkboxes = this.optionsTarget.querySelectorAll(
      'input[type="checkbox"]',
    );
    checkboxes.forEach((cb) => (cb.checked = false));
    this.updateLabel();

    // Clear all pills if present
    if (this.hasPillsTarget) {
      this.pillsTarget.innerHTML = "";
      this.updatePillsContainerPadding();
    }
  }

  // Legacy method for backwards compatibility
  removeLot(event) {
    event.preventDefault();
    event.stopPropagation();
    const lotId = event.currentTarget.dataset.multiselectDropdownLotIdValue;
    const checkbox = this.optionsTarget.querySelector(
      `input[type="checkbox"][value="${lotId}"]`,
    );
    if (checkbox) {
      checkbox.checked = false;
      this.updateLabel();
    }
  }
}
